import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import {QUESTION_BOX_TEXT} from '../componentHelp';
import RichEditorResultText from './Shared/RichEditorResultText';
import {generateUUID} from '../../../../../utility/uuidUtil';
import {FormBuilderComponentTypes} from '../ComponentList';

export const CALCULATION_OPERAND_LIST_KEY = 'operands';

export const CALCULATION_SOURCE_TYPES = [
    FormBuilderComponentTypes.NumericInputBox,
    FormBuilderComponentTypes.Calculation
];

const OPERATIONS = [
    {value: 'add', title: 'Add'},
    {value: 'subtract', title: 'Subtract'}
];

export const getCalculationSourceOptions = (schema, componentId) => {
    if (!Array.isArray(schema)) return [];

    const position = schema.findIndex(component => component.id === componentId);

    return schema
        .filter((component, componentIndex) =>
            CALCULATION_SOURCE_TYPES.includes(component.type) &&
            component.id !== componentId &&
            (position === -1 || componentIndex < position));
};

export const calculateCalculationResult = (operands, schema, answers) => {
    if (!Array.isArray(operands) || operands.length === 0) return '';

    let total = 0;

    for (const operand of operands) {
        if (!operand?.componentId) return '';

        const source = (schema ?? []).find(component => component.id === operand.componentId);

        if (!source) return '';

        const answer = answers?.[operand.componentId];

        if (answer === undefined || answer === null || answer === '') return '';

        const number = Number(answer);

        if (!Number.isFinite(number)) return '';

        total = operand.operation === 'subtract' ? total - number : total + number;
    }

    return Number(total.toFixed(10));
};

const Calculation = ({
                         index,
                         componentId,
                         onChange,
                         descriptionHtml,
                         onDescriptionHtmlChange,
                         commitDescriptionHtml,
                         isEditMode,
                         isFormArchived,
                         readonly,
                         placeholderValues,
                         accessLevel,
                         submitMode,
                         schema,
                         answers,
                         operands,
                         value,
                         audience,
                         recipientType,
                         linkedToStudents,
                         linkedToEmployees
                     }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml ?? '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml ?? '');
    }, [descriptionHtml, isEditorFocused]);

    const sourceOptions = useMemo(
        () => getCalculationSourceOptions(schema, componentId),
        [schema, componentId]
    );

    const result = useMemo(
        () => calculateCalculationResult(operands, schema, answers),
        [operands, schema, answers]
    );

    useEffect(() => {
        if (isEditMode || readonly) return;

        const current = value === undefined || value === null ? '' : value;

        if (current === result) return;

        onChange({
            id: componentId,
            value: result
        });
    }, [result, value, isEditMode, readonly, componentId]);

    const handleDraftDescriptionChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleEditorBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleAddOperand = () => onChange({
        index,
        listKey: CALCULATION_OPERAND_LIST_KEY,
        isAdd: true,
        option: {
            id: generateUUID(),
            componentId: '',
            operation: 'add'
        }
    });

    const handleRemoveOperand = (operandId) => onChange({
        index,
        listKey: CALCULATION_OPERAND_LIST_KEY,
        isRemove: true,
        optionId: operandId
    });

    const handleOperandChange = (operandId, changes) => onChange({
        index,
        listKey: CALCULATION_OPERAND_LIST_KEY,
        optionId: operandId,
        option: changes
    });

    const displayedValue = readonly
        ? (value === undefined || value === null ? '' : value)
        : (result === '' ? '' : result);

    if (!isEditMode) {
        return (
            <Box key={`Calculation-${componentId}`} sx={{mt: 2}}>
                <RichEditorResultText
                    id={componentId}
                    description={descriptionHtml}
                    placeholderValues={placeholderValues}
                    readonly={readonly}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
                <TextField
                    fullWidth
                    value={displayedValue.toString()}
                    InputProps={{readOnly: true}}
                    disabled
                    sx={{
                        mt: 1,
                        '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'black',
                            color: 'black',
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box key={`Calculation-${componentId}`} sx={{mt: 2}}>
            <Box sx={{mb: 1}}>
                <RichEditor
                    index={index}
                    isEditMode
                    isFormLive={isFormArchived}
                    accessLevel={accessLevel}
                    currentDescription={draftDescriptionHtml}
                    setCurrentDescription={handleDraftDescriptionChange}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    placeholderValues={placeholderValues}
                    onFocus={() => setIsEditorFocused(true)}
                    onBlur={handleEditorBlur}
                    // AD-2689 (Maria): says what this box is for on a calculation.
                    emptyText={QUESTION_BOX_TEXT.calculation}
                />
            </Box>

            <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    The answer is worked out from the numeric questions above this one. Whoever completes the
                    form sees the result but cannot change it.
                </Typography>

                {sourceOptions.length === 0 && (
                    <Typography variant="body2" color="error">
                        Add a Numeric Input question above this one before setting up the calculation.
                    </Typography>
                )}

                {(operands ?? []).map((operand, operandIndex) => {
                    const isMissing = operand.componentId &&
                        !sourceOptions.some(option => option.id === operand.componentId);

                    return (
                        <Stack
                            key={operand.id ?? operandIndex}
                            flexDirection={{
                                xs: 'column',
                                sm: 'row'
                            }}
                            gap={2}
                            alignItems="center"
                        >
                            <TextField
                                select
                                label="Operation"
                                value={operand.operation ?? 'add'}
                                disabled={isFormArchived}
                                sx={{minWidth: 160}}
                                onChange={(e) => handleOperandChange(operand.id, {operation: e.target.value})}
                            >
                                {OPERATIONS.map(operation => (
                                    <MenuItem key={operation.value} value={operation.value}>
                                        {operation.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label="Question"
                                required
                                fullWidth
                                error={!!isMissing}
                                helperText={isMissing ? 'This question is no longer above this one on the form' : ''}
                                value={isMissing ? '' : (operand.componentId ?? '')}
                                disabled={isFormArchived || sourceOptions.length === 0}
                                onChange={(e) => handleOperandChange(operand.id, {componentId: e.target.value})}
                            >
                                {sourceOptions.map(option => (
                                    <MenuItem key={option.id} value={option.id} sx={{maxWidth: 560}}>
                                        <Box
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={option.description || option.label}
                                        >
                                            {option.description || option.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                                {sourceOptions.length === 0 && (
                                    <MenuItem disabled>No numeric questions available</MenuItem>
                                )}
                            </TextField>
                            <Tooltip title="Remove">
                                <span>
                                    <IconButton
                                        color="error"
                                        aria-label="Remove calculation question"
                                        disabled={isFormArchived}
                                        onClick={() => handleRemoveOperand(operand.id)}
                                    >
                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    );
                })}

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddRoundedIcon/>}
                    disabled={isFormArchived}
                    sx={{alignSelf: 'flex-start'}}
                    onClick={handleAddOperand}
                >
                    Add a Numeric Input to the calculation
                </Button>
            </Stack>
        </Box>
    );
};

Calculation.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    index: PropTypes.number,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    schema: PropTypes.array,
    answers: PropTypes.object,
    operands: PropTypes.array,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
};

export default Calculation;
